import {Form} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useSyncExternalStore} from 'react';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {getBulkOperationTarget, type BulkOperationActionCallbackParams} from 'features/eag-grid';

import {getVehicleAdvertisingBulkParams} from './getVehicleAdvertisingBulkParams';
import {VehicleAdvertisingBulkFormFooter} from './VehicleAdvertisingBulkFormFooter';
import {VehicleAdvertisingPlatformsField} from './VehicleAdvertisingPlatformsField';
import {VehicleAdvertisingScopeField} from './VehicleAdvertisingScopeField';

const VehicleAdvertisingBulkFormSchema = z.object({platforms: z.array(z.string().min(1)).min(1)});
const VehicleAdvertisingBulkConfirmSchema = z.object({platforms: z.array(z.string())});

export function VehicleAdvertisingBulkForm(props: {action: BulkOperationActionCallbackParams}) {
  const context = props.action.context;
  const state = useSyncExternalStore(context.subscribe, context.getSnapshot);
  const target = getBulkOperationTarget(state);
  const requiresPlatforms = props.action.actionKey !== 'SALES_DISABLE_VEHICLE_IN_ADVERTISEMENT';
  const field = props.action.paramsSchema?.root.elements.find(
    (candidate) => candidate.name === 'platforms' && candidate.renderer === 'select'
  );
  const options = field?.defaultOptions ?? [];
  const enabledPlatforms = options
    .filter((option) => !option.isDisabled)
    .map((option) => option.value);
  const vehicleCount =
    state.scope === 'selected'
      ? state.selection.recordIds.length
      : (state.selection.query.totalCount ?? 0);
  const handleSubmit = async (values: z.infer<typeof VehicleAdvertisingBulkFormSchema>) => {
    await context
      .submit(
        requiresPlatforms
          ? getVehicleAdvertisingBulkParams(props.action.actionKey, values.platforms)
          : null
      )
      .catch(handleApiError);
  };

  return (
    <Form
      experimentalZodSchema={
        requiresPlatforms ? VehicleAdvertisingBulkFormSchema : VehicleAdvertisingBulkConfirmSchema
      }
      defaultValues={{platforms: []}}
      isDisabled={state.isSubmitting}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={6}>
          <Show
            when={
              isNotNilOrEmpty(props.action.actionDefinition.description)
                ? props.action.actionDefinition.description
                : null
            }
          >
            {(description) => <Text color="secondary">{description}</Text>}
          </Show>
          <VehicleAdvertisingScopeField context={context} />
          <Show when={requiresPlatforms}>
            <VehicleAdvertisingPlatformsField
              control={control}
              label={field?.label ?? i18n.t('entity.vehicle.labels.advertisementTabPlatforms')}
              options={options}
            />
          </Show>
          <VehicleAdvertisingBulkFormFooter
            control={control}
            action={props.action}
            vehicleCount={vehicleCount}
            requiresPlatforms={requiresPlatforms}
            platformLabel={
              field?.label ?? i18n.t('entity.vehicle.labels.advertisementTabPlatforms')
            }
            isDisabled={
              isNil(target) ||
              (requiresPlatforms && (isNil(field) || enabledPlatforms.length === 0))
            }
          />
        </VStack>
      )}
    </Form>
  );
}
